import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { select, Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  take,
} from 'rxjs';

import {
  BookingAppState,
  MultiCityOffer,
  MultiCityStatus,
  OffersPerBound,
  SelectionPhase,
} from '@fcom/common/interfaces/booking';
import {
  ButtonPosition,
  ButtonSize,
  ButtonTheme,
  ModalButtons,
  NotificationTheme,
  TabLayoutType,
} from '@fcom/ui-components';
import { GlobalBookingFlight } from '@fcom/common/store';
import { BUS_AIRCRAFT_CODE, ExtendedFareFamily, TRAIN_AIRCRAFT_CODE } from '@fcom/common-booking/interfaces';
import { GtmEvent, LoaderType } from '@fcom/common/interfaces';
import { LoginActions } from '@fcom/core/actions';
import { LoginStep } from '@fcom/core-api/login';
import { Bound, FinnairCabinClass, FinnairLocation } from '@fcom/dapi/api/models';
import { Amount, BoundType, PaxAmount } from '@fcom/dapi';
import { isFlight, priorityServicesAvailable } from '@fcom/common-booking/utils/common-booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { getGtmMulticityOffertSelectionData } from '@fcom/common/utils/gtm.utils';

import { BookingMultiCityService } from '../../services';
import {
  selectMultiCityAvailableFareFamilies,
  selectMultiCityOffers,
  selectMultiCitySelectedBounds,
  selectMultiCitySelectedFareFamily,
  selectMultiCitySelectedItinerary,
  selectMultiCityStatus,
} from '../../../../store/selectors';

@Component({
  selector: 'fin-multi-city-bounds',
  templateUrl: './multi-city-bounds.component.html',
  styleUrls: ['./multi-city-bounds.component.scss'],
  standalone: false,
})
export class MultiCityBoundsComponent implements OnInit {
  readonly BoundType = BoundType;
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly LoaderType = LoaderType;
  readonly ModalButtons = ModalButtons;
  readonly MultiCityStatus = MultiCityStatus;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TabLayoutType = TabLayoutType;
  readonly FinnairCabinClass = FinnairCabinClass;

  modalOpen = false;

  private subscriptions = new Subscription();
  currentBoundDetails$: Subject<OffersPerBound> = new Subject<OffersPerBound>();
  activeTab$ = new Subject<number>();
  upgradeTabSelected$: Observable<boolean>;
  selectionPhase$ = of(SelectionPhase.MULTICITY);

  offers$: Observable<MultiCityOffer[]>;
  matchingOffers$: Observable<MultiCityOffer[]>;
  selectedFareFamily$: Observable<ExtendedFareFamily>;
  selectedItinerary$: Observable<string[]>;
  selectedBounds$: Observable<Bound[]>;
  offersPerBound$: Observable<OffersPerBound[][]>;
  currency$: Observable<string>;
  selectedOffer$ = new BehaviorSubject<OffersPerBound>(null);
  offersStatus$: Observable<MultiCityStatus>;
  cheapestMatchingOffer$: Observable<{
    price: string;
    offer: MultiCityOffer;
  }>;
  fareFamilies$: Observable<ExtendedFareFamily[]>;
  selectedOfferPrice$: Observable<Amount>;
  hasBusLeg$: Observable<boolean[]>;
  hasTrainSegment$: Observable<boolean[]>;
  priorityServicesAvailable$: Observable<boolean[]>;

  @Input({ required: true }) amountOfBounds$: Observable<number>;
  @Input({ required: true }) currentBound$: Observable<number>;
  @Input({ required: true }) flights$: Observable<GlobalBookingFlight[]>;
  @Input({ required: true }) paxAmount$: Observable<PaxAmount>;

  @Output() continueToNextBound = new EventEmitter<OffersPerBound>();
  @Output() resetSelection = new EventEmitter();
  @Output() resetToBound = new EventEmitter<{
    bound: Bound;
    index: number;
  }>();
  locations$: Observable<{ [key: string]: FinnairLocation }>;

  constructor(
    private store$: Store<BookingAppState>,
    private bookingMultiCityService: BookingMultiCityService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.offers$ = this.store$.pipe(select(selectMultiCityOffers), filter(Boolean));
    this.selectedItinerary$ = this.store$.pipe(select(selectMultiCitySelectedItinerary));
    this.offersStatus$ = this.store$.pipe(select(selectMultiCityStatus));
    this.fareFamilies$ = this.store$.pipe(select(selectMultiCityAvailableFareFamilies));
    this.offersPerBound$ = this.bookingMultiCityService.getOffersPerBounds();
    this.currency$ = this.bookingMultiCityService.getCurrency();
    this.locations$ = this.bookingMultiCityService.locations$;
    this.hasBusLeg$ = combineLatest([this.offersPerBound$, this.currentBound$]).pipe(
      filter(([_bound, index]) => {
        return index >= 1;
      }),
      map(([bound, index]) =>
        bound[index - 1].map((b) =>
          b.boundInfo.itinerary.filter(isFlight).some((i) => i.aircraft?.code?.toUpperCase() === BUS_AIRCRAFT_CODE)
        )
      )
    );

    this.hasTrainSegment$ = combineLatest([this.offersPerBound$, this.currentBound$]).pipe(
      filter(([_bound, index]) => {
        return index >= 1;
      }),
      map(([bound, index]) =>
        bound[index - 1].map((b) =>
          b.boundInfo.itinerary.filter(isFlight).some((i) => i.aircraft?.code?.toUpperCase() === TRAIN_AIRCRAFT_CODE)
        )
      )
    );
    this.priorityServicesAvailable$ = combineLatest([this.offersPerBound$, this.currentBound$]).pipe(
      filter(([_bound, index]) => {
        return index >= 1;
      }),
      map(([bound, index]) => bound[index - 1].map((b) => priorityServicesAvailable(b.boundInfo.itinerary)))
    );

    this.selectedFareFamily$ = combineLatest([
      this.fareFamilies$,
      this.store$.pipe(select(selectMultiCitySelectedFareFamily)),
    ]).pipe(
      map(([fareFamilies, selectedFareFamily]) => {
        return fareFamilies?.find((fareFamily) => {
          return fareFamily.fareFamilyCode === selectedFareFamily;
        });
      })
    );

    this.selectedOfferPrice$ = combineLatest([this.selectedOffer$, this.currency$]).pipe(
      map(([selectedOffer, currency]) => ({
        amount: selectedOffer?.fareFamily?.totalPrice,
        currencyCode: currency,
      }))
    );

    this.matchingOffers$ = combineLatest([this.selectedItinerary$, this.offers$, this.selectedFareFamily$]).pipe(
      map(([selectedItinerary, offers, selectedFareFamily]) => {
        return offers.filter((offer) => {
          const hasCorrectItinerary = selectedItinerary.every((itinerary, i) => {
            return itinerary === offer.itinerary[i];
          });

          const hasCorrectFareFamily = Object.values(offer.offerItems[0].fareFamilyCodePerBound).includes(
            selectedFareFamily?.fareFamilyCode
          );

          return hasCorrectItinerary && hasCorrectFareFamily;
        });
      })
    );

    this.cheapestMatchingOffer$ = combineLatest([this.matchingOffers$, this.selectedOffer$]).pipe(
      map(([offers, selectedOffer]) => {
        return this.bookingMultiCityService.findCheapestOffer(selectedOffer ? [selectedOffer.cheapestOffer] : offers);
      })
    );

    this.selectedBounds$ = this.store$.pipe(select(selectMultiCitySelectedBounds));

    this.upgradeTabSelected$ = this.activeTab$.pipe(map((index) => index === 1));

    this.subscriptions.add(
      this.currentBound$.pipe(distinctUntilChanged()).subscribe((currentBound) => {
        if (currentBound > 1) {
          this.findMoreFlights(currentBound - 1);
        }
      })
    );
  }

  clickUniqueItinerary(offer: OffersPerBound): void {
    if (this.selectedOffer$.getValue()?.itineraryId === offer.itineraryId) {
      this.selectedOffer$.next(null);
    } else {
      this.selectedOffer$.next(offer);
    }
  }

  findMoreFlights(boundIndex: number): void {
    this.subscriptions.add(
      this.matchingOffers$.pipe(take(1)).subscribe((matchingOffers) => {
        const cheapestOffer = this.bookingMultiCityService.findCheapestOffer(matchingOffers);

        this.bookingMultiCityService.fetchMultiCityMoreFlights(cheapestOffer.offer, boundIndex);
      })
    );
  }

  continueWithSelection(): void {
    this.subscriptions.add(
      combineLatest([this.selectedOffer$, this.currentBound$, this.currency$, this.paxAmount$])
        .pipe(take(1))
        .subscribe(([offer, currentBound, currency, paxAmount]) => {
          this.continueToNextBound.emit(offer);
          const data = getGtmMulticityOffertSelectionData(offer, currentBound, currency, paxAmount);
          this.gtmService.multicityOfferSelection(GtmEvent.FLIGHT_SELECTION_CLICK, data);
          this.selectedOffer$.next(null);
        })
    );
  }

  openViewDetails(bound: OffersPerBound): void {
    this.modalOpen = true;
    this.currentBoundDetails$.next(bound);
  }

  trackByFunction(index: number): number {
    return index;
  }

  triggerLogin(): void {
    this.modalOpen = false;
    this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
  }

  onModalClose(): void {
    this.currentBoundDetails$.next(null);
  }
}
